import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppShell, Topbar } from "@marekl/adminer/shell";
import { AdminerToaster, OfflineBanner } from "@marekl/adminer/components";
import { useAuth } from "../../auth/useAuth";
import { useLogout } from "../../auth/queries";
import { Sidebar } from "../Sidebar";
import { InstanceTag } from "../InstanceTag";
import { ThemeToggle } from "../ThemeToggle";

/** Breadcrumb label for each top-level route. */
const ROUTE_LABELS: Record<string, string> = {
  "/dashboard": "Overview",
  "/messages": "Messages",
  "/batches": "Batches",
  "/templates": "Templates",
  "/senders": "Senders",
  "/suppressions": "Suppressions",
  "/api-keys": "API keys",
  "/workers": "Workers",
  "/webhooks": "Webhooks",
  "/audit-log": "Audit log",
  "/settings": "Settings",
};

/**
 * Section-level default crumb: exact route first, then the longest prefix so
 * nested routes (e.g. "/templates/123") resolve to their section label.
 * Pages needing a more specific trail call `useSetTopbarCrumbs` themselves.
 */
function routeLabel(pathname: string): string {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  const section = Object.keys(ROUTE_LABELS)
    .filter((path) => pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  return section ? ROUTE_LABELS[section] : "Overview";
}

export function AppLayout() {
  const { user } = useAuth();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;

  function handleLogout() {
    logout(undefined, { onSettled: () => navigate("/login", { replace: true }) });
  }

  return (
    <AppShell
      sidebar={<Sidebar user={user} onLogout={handleLogout} />}
      topbar={
        <Topbar
          crumbs={[{ label: "Letterman", href: "/dashboard" }, { label: routeLabel(location.pathname) }]}
          right={
            <>
              <InstanceTag />
              <ThemeToggle />
            </>
          }
        />
      }
      toaster={<AdminerToaster position="bottom-right" toastOptions={{ duration: 4000 }} />}
      navigationKey={location.pathname}
      // The bar spans the corner, which is what lets the rack collapse to a
      // rail AND keeps the toggle in the topbar: the shell hands the button to
      // the sidebar only when an inset topbar sits beside a rail, where it
      // would travel ~184px left on every collapse.
      topbarAboveSidebar
      collapsible
      collapseTo="rail"
      persistCollapsedKey="letterman.sidebar"
    >
      <OfflineBanner position="top" />
      <Outlet />
    </AppShell>
  );
}
